import React from 'react';
import { motion } from 'framer-motion';
import PlayingCard from '@/components/PlayingCard';
import { TID } from '@/constants/testIds';
import { useDeferredScores } from '@/hooks/useDeferredScores';
import { gameplayPresentation } from '@/config/presentationSlots';

/**
 * PHASE 5.0B-4 — PENALTY SHOOTOUT STATIC VISUAL MIGRATION.
 *
 * Presentation-only re-skin of the existing simultaneous penalty view.
 *  • Reads authoritative state exclusively (state.penalty, timer).
 *  • Uses the same PENALTY_PICK_ATTACK / PENALTY_PICK_DEFENSE dispatch
 *    actions and same test IDs as before.
 *  • Uses SHARED card geometry vars (--kk-slot-w / --kk-card-w) so
 *    penalty cards render at pixel-identical size to the main board.
 *  • Cinematic scene switches with role: SHOOT scene when local player
 *    is shooter, DEFEND scene when local player is defender.
 *  • NO swipe / power / left-center-right / aim mechanic. The
 *    football scene is background only — cards remain the sole input.
 *  • NO avatar / profile artwork. Player identity is text-only.
 *
 * Props (unchanged): state, dispatch, myPlayerId, opponentName,
 * matchFinished, timer, serverClockSkew.
 */
const A = gameplayPresentation.assets;

export default function PenaltyView({
  state, dispatch,
  myPlayerId = 'p1', opponentName,
  matchFinished = false,
  timer = null, serverClockSkew = 0,
  // Authoritative reveal window (ms epoch). While Date.now() is less
  // than this, the current lastShot reveal overlay is shown. Once the
  // window elapses the reveal unmounts BEFORE the next round's cards
  // become the active input surface. Owned by GameScreen /
  // OnlineGameScreen (see `penaltyRevealUntil` state there); do NOT
  // touch pen.shots — the shot itself remains in history.
  penaltyRevealUntil = 0,
}) {
  const pen = state.penalty;
  const displayScores = useDeferredScores(pen?.scores?.p1 ?? 0, pen?.scores?.p2 ?? 0, false);

  // Live tick for both timers only. The reveal window uses its own
  // deterministic setTimeout gate below — never rely on setInterval
  // throttling (mobile Safari can throttle background/idle intervals
  // and delay the reveal-unmount past the authoritative hold).
  const [nowTick, setNowTick] = React.useState(Date.now());
  const shooterBase = timer?.penaltyShooterBaseDeadline ?? timer?.penaltyBaseDeadline ?? null;
  const defenderBase = timer?.penaltyDefenderBaseDeadline ?? null;
  React.useEffect(() => {
    if (!shooterBase && !defenderBase) return undefined;
    const id = setInterval(() => setNowTick(Date.now()), 200);
    return () => clearInterval(id);
  }, [shooterBase, defenderBase]);

  // BUG-1 fix (mobile stale reveal): deterministic reveal-visibility
  // state driven by ONE authoritative setTimeout. When a new shot
  // arrives (pen.shots.length grows) OR the parent moves the reveal
  // horizon forward, we open the reveal for the current shot index
  // and schedule its close at `penaltyRevealUntil`. Once the timeout
  // fires the reveal is UNCONDITIONALLY unmounted — no dependency on
  // setInterval frequency or React scheduler throttling. The shot
  // itself remains untouched in `pen.shots` (dots + score keep
  // reading from it).
  const shotCount = pen?.shots?.length ?? 0;
  const [revealingShotIdx, setRevealingShotIdx] = React.useState(-1);
  React.useEffect(() => {
    if (shotCount <= 0) {
      setRevealingShotIdx((cur) => (cur !== -1 ? -1 : cur));
      return undefined;
    }
    const currentIdx = shotCount - 1;
    const remaining = Math.max(0, penaltyRevealUntil - Date.now());
    if (penaltyRevealUntil <= 0 || remaining <= 0) {
      // Window not open (or already elapsed) → make sure reveal is off.
      setRevealingShotIdx((cur) => (cur !== -1 ? -1 : cur));
      return undefined;
    }
    // Open reveal for THIS shot index and schedule its close.
    setRevealingShotIdx(currentIdx);
    const t = setTimeout(() => setRevealingShotIdx(-1), remaining + 50);
    return () => clearTimeout(t);
  }, [shotCount, penaltyRevealUntil]);
  if (!pen) return null;

  const shooter = pen.currentShooter;
  const defender = shooter === 'p1' ? 'p2' : 'p1';
  const isMeShooter = shooter === myPlayerId;
  const myRole = isMeShooter ? 'shooter' : 'defender';
  const oppLabel = (opponentName || 'OPPONENT').toUpperCase();
  const myLabel  = ((state.players?.[myPlayerId]?.name) || 'YOU').toUpperCase();

  const myHand = isMeShooter
    ? { cards: pen.hands[myPlayerId]?.attacks || [], usedList: pen.used[myPlayerId]?.attacks || [] }
    : { cards: pen.hands[myPlayerId]?.defenses || [], usedList: pen.used[myPlayerId]?.defenses || [] };
  const oppHand = isMeShooter
    ? { cards: pen.hands[defender]?.defenses || [], usedList: pen.used[defender]?.defenses || [] }
    : { cards: pen.hands[shooter]?.attacks || [], usedList: pen.used[shooter]?.attacks || [] };

  const locks = pen.locks || {
    shooter: { index: null, locked: pen.selection?.attackIndex != null },
    defender: { index: null, locked: pen.selection?.defenseIndex != null },
  };
  const myLock = locks[myRole] || { index: null, locked: false };
  const oppLock = locks[myRole === 'shooter' ? 'defender' : 'shooter'] || { index: null, locked: false };
  const iLocked = !!myLock.locked;
  const oppLocked = !!oppLock.locked;

  const lastShot = pen.shots[pen.shots.length - 1];
  // BUG-1 hardened (mobile stale reveal): reveal visibility now
  // requires ALL THREE authoritative conditions:
  //   (A) `revealingShotIdx` matches the current last shot — set by
  //       the setTimeout gate; cleared exactly when the authoritative
  //       hold elapses.
  //   (B) `penaltyRevealUntil > Date.now()` — a hard, timer-independent
  //       invariant re-evaluated on every render. If the setTimeout
  //       callback is throttled by mobile Safari, any subsequent
  //       re-render (parent tick, prop change, state change) will
  //       still evaluate this to false and unmount the overlay.
  //   (C) `!newChoiceInProgress` — the LOCAL player has NOT yet
  //       interacted with the next round's tray (they haven't locked
  //       a new pick, and the round counter still matches). If the
  //       local player has already locked for the NEXT decision, the
  //       reveal is definitely stale and must be gone regardless of
  //       any timer state.
  // Any single false condition unmounts the overlay. `pen.shots`
  // history is untouched — dots/score keep reading from it.
  const localHasLockedNext = !!(pen.locks && pen.locks[myRole]?.locked);
  const revealActive = !!lastShot
    && revealingShotIdx === (pen.shots.length - 1)
    && revealingShotIdx >= 0
    && penaltyRevealUntil > 0
    && Date.now() < penaltyRevealUntil
    && !localHasLockedNext;

  // Build per-player result-dot arrays from authoritative shot history.
  // `outcome === 'goal'` means the shooter scored. From the shooter's
  // own perspective it's a `goal` dot; the defender never has their
  // own dot for that shot. From the shooter's perspective a `save`
  // outcome means they failed to score → `miss` dot. The current
  // engine never emits an off-target `miss` — save covers all fails.
  const dotsFor = (pid) => {
    const shots = (pen.shots || []).filter((s) => s.shooter === pid);
    return shots.map((s) => (s.outcome === 'goal' ? 'goal' : 'miss'));
  };
  const p1Dots = dotsFor('p1');
  const p2Dots = dotsFor('p2');
  // Empty-slot count: match up to 5 rounds baseline; if sudden death
  // is active we render only the shots taken so far without padding.
  const emptyCount = pen.suddenDeath
    ? 0
    : Math.max(0, 5 - Math.max(p1Dots.length, p2Dots.length));

  const now = nowTick - (serverClockSkew || 0);
  const myBaseDL = isMeShooter ? shooterBase : defenderBase;
  const myBankDL = isMeShooter
    ? (timer?.penaltyShooterBankDeadline ?? timer?.penaltyBankDeadline ?? null)
    : (timer?.penaltyDefenderBankDeadline ?? null);
  const usingBank = myBaseDL != null && now >= myBaseDL;
  const activeDL = usingBank ? myBankDL : myBaseDL;
  const timerRemainingSec = activeDL != null
    ? Math.max(0, Math.ceil((activeDL - now) / 1000))
    : null;

  const roleTag = isMeShooter ? 'SHOOTING' : 'DEFENDING';
  const sceneSrc = isMeShooter ? A.penaltySceneShoot : A.penaltySceneDefend;
  const titleText = isMeShooter ? 'CHOOSE YOUR SHOT' : 'OPPONENT SHOOTING';
  const titleModifier = isMeShooter ? 'shoot' : 'defend';
  const helperText = isMeShooter
    ? 'TAP ONE OF YOUR ATTACK CARDS TO LOCK YOUR STRIKE.'
    : 'CHOOSE ONE OF YOUR GOALKEEPERS.';
  const trayTitle = isMeShooter ? 'YOUR ATTACK' : 'YOUR GOALKEEPERS';

  const canPick = !iLocked && !matchFinished && !pen.finished;
  const pickAction = isMeShooter ? 'PENALTY_PICK_ATTACK' : 'PENALTY_PICK_DEFENSE';
  const onPickCard = (i) => {
    if (!canPick) return;
    if (myHand.usedList.includes(i)) return;
    dispatch({ type: pickAction, index: i });
  };

  return (
    <div
      data-testid={TID.penaltyScreen}
      data-role={myRole}
      // Reuse the app's portrait shell (kickard-frame is-match) so on
      // desktop the entire penalty experience is a centred ~430px
      // portrait game frame instead of stretching across the browser.
      className="kk-penalty-root kickard-frame is-match flex flex-col"
      style={{
        // Inject asset URLs as CSS custom properties. CSS uses them via
        // `background-image: var(...)`. This bypasses CRA's css-loader
        // resolving `/penalty/*` as module paths.
        '--penalty-hud-shell':      `url(${A.penaltyHudShell})`,
        '--penalty-tray-shell':     `url(${A.penaltyCardTrayShell})`,
        '--penalty-selected-frame': `url(${A.penaltySelectedCardFrame})`,
        '--penalty-divider-glow':   `url(${A.penaltyDividerGlow})`,
      }}
    >
      {/* ========================================================
          TOP PENALTY HUD — dynamic content over SVG shell.
          ======================================================== */}
      <div className="kk-penalty-hud">
        <div className="kk-penalty-hud__row">
          <div className="kk-penalty-hud__side kk-penalty-hud__side--left">
            <div className="kk-penalty-hud__name">
              <span className="kk-penalty-hud__name-tag">YOU ·</span>{myLabel}
            </div>
          </div>
          <div className="kk-penalty-hud__score">
            <span
              data-testid="penalty-score-p1"
              className="kk-penalty-hud__score-p1"
            >{displayScores.p1}</span>
            <span className="kk-penalty-hud__score-sep">—</span>
            <span
              data-testid="penalty-score-p2"
              className="kk-penalty-hud__score-p2"
            >{displayScores.p2}</span>
          </div>
          <div className="kk-penalty-hud__side kk-penalty-hud__side--right">
            <div className="kk-penalty-hud__name">{oppLabel}</div>
          </div>
        </div>

        <div className="kk-penalty-hud__meta">
          <DotStrip
            side="left"
            resolved={p1Dots}
            empty={emptyCount}
          />
          <div className="kk-penalty-hud__meta-center">
            <div>PENALTY SHOOTOUT</div>
            <div className="kk-penalty-hud__meta-round">
              {pen.suddenDeath ? 'SUDDEN DEATH' : `ROUND ${Math.min(pen.round, 5)} OF 5`}
            </div>
          </div>
          <DotStrip
            side="right"
            resolved={p2Dots}
            empty={emptyCount}
          />
        </div>
      </div>

      {/* ========================================================
          OPPONENT HIDDEN STRIP — text-only identity, small quiet
          card backs, uses shared card geometry.
          ======================================================== */}
      <div className="kk-penalty-opphand">
        <div className="kk-penalty-opphand__title">
          {isMeShooter ? `${oppLabel} · GK` : `${oppLabel} · SHOTS`}
        </div>
        <div className="kk-penalty-opphand__row">
          {oppHand.cards.map((c, i) => {
            const used = oppHand.usedList.includes(i);
            const highlightAsRevealed = lastShot && (
              (isMeShooter && lastShot.defenseCard?.id === c?.id) ||
              (!isMeShooter && lastShot.attackCard?.id === c?.id)
            );
            return (
              <div
                key={`opp-${i}`}
                data-testid={isMeShooter
                  ? TID.penaltyDefenseCard(i)
                  : TID.penaltyAttackCard(i)}
                className={`kk-penalty-opphand__slot ${used ? 'is-disabled' : ''}`}
                style={{ opacity: used ? 0.35 : 1 }}
              >
                <PlayingCard card={null} faceDown small penaltyBack highlight={highlightAsRevealed} />
              </div>
            );
          })}
        </div>
      </div>

      {/* ========================================================
          CINEMATIC SCENE — dominates middle. shoot vs defend.
          NEVER captures input. Cards below are the sole input.
          ======================================================== */}
      <div className="kk-penalty-scene">
        <img
          key={sceneSrc}
          src={sceneSrc}
          alt=""
          aria-hidden
          className="kk-penalty-scene__img"
          data-testid={isMeShooter ? 'penalty-scene-shoot' : 'penalty-scene-defend'}
        />
        <div className="kk-penalty-scene__dim" />
        <div className="kk-penalty-scene__overlay">
          {!revealActive && (
            <>
              <div
                data-testid={TID.penaltyShooter}
                data-role-tag={roleTag}
                className={`kk-penalty-scene__title kk-penalty-scene__title--${titleModifier}`}
              >
                {titleText}
              </div>
              <div className="kk-penalty-scene__divider" />
              {!iLocked && (
                <div className="kk-penalty-scene__helper">
                  {helperText}
                </div>
              )}
              {!matchFinished && !pen.finished && !iLocked && timerRemainingSec != null && (
                <div
                  data-testid="penalty-countdown"
                  data-using-bank={usingBank ? 'true' : 'false'}
                  className="kk-penalty-scene__countdown"
                >
                  {usingBank ? `TIME BANK · ${timerRemainingSec}s` : `${timerRemainingSec}s`}
                </div>
              )}
              {iLocked && (
                <div data-testid="penalty-locked-in" className="mt-2 flex flex-col items-center">
                  <div className="kk-penalty-scene__locked">LOCKED IN</div>
                  <div className="kk-penalty-scene__wait">
                    {oppLocked ? 'REVEALING…' : 'WAITING FOR OPPONENT…'}
                  </div>
                </div>
              )}
              {oppLocked && !iLocked && !matchFinished && !pen.finished && (
                <div
                  data-testid="penalty-opponent-locked"
                  className="kk-penalty-scene__opponent-locked"
                >
                  {oppLabel} LOCKED IN
                </div>
              )}
            </>
          )}
        </div>
        {revealActive && (
          <RevealBlock lastShot={lastShot} matchFinished={matchFinished} />
        )}
      </div>

      {/* ========================================================
          CARD TRAY — five REAL PlayingCards, shared slot geometry,
          selected card highlighted with SVG frame via CSS ::after.
          Existing selection handler untouched.
          ======================================================== */}
      <div className="kk-penalty-tray">
        <div className="kk-penalty-tray__title">{trayTitle}</div>
        <div className="kk-penalty-tray__row" data-testid="penalty-hand-row">
          {myHand.cards.map((c, i) => {
            const used = myHand.usedList.includes(i);
            const selected = iLocked && myLock.index === i;
            const testid = isMeShooter
              ? TID.penaltyAttackCard(i)
              : TID.penaltyDefenseCard(i);
            const usedTestid = isMeShooter
              ? TID.penaltyAttackUsed(i)
              : null;
            return (
              <button
                key={`mine-${i}`}
                type="button"
                disabled={!canPick || used || iLocked}
                data-testid={testid}
                data-selected={selected ? 'true' : 'false'}
                onClick={() => onPickCard(i)}
                className={`kc-slot ${selected ? 'is-selected' : ''} ${used ? 'is-disabled' : ''}`}
              >
                <div className="w-full h-full">
                  <PlayingCard card={c} small penaltyBack highlight={selected} />
                </div>
                {used && usedTestid && (
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                    <div
                      className="font-heading text-[9px] tracking-[0.2em] uppercase rotate-[-14deg] px-1 py-0.5 bg-black/70 border border-[hsl(var(--k-red)/0.7)] text-[hsl(var(--k-red))] rounded"
                      data-testid={usedTestid}
                    >
                      USED
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div className="kk-penalty-tray__footer">
          {matchFinished ? (
            <div
              data-testid="penalty-final-reveal-hold"
              style={{ color: 'var(--kk-game-gold-bright)', letterSpacing: '0.28em' }}
            >
              {lastShot?.outcome === 'goal' ? 'WINNING KICK' : 'DECIDING SAVE'}
            </div>
          ) : iLocked && !oppLocked ? (
            <div data-testid="penalty-waiting">WAITING FOR OPPONENT…</div>
          ) : iLocked && oppLocked ? (
            <div style={{ color: 'var(--kk-game-gold-bright)' }}>REVEALING…</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// DotStrip — renders resolved goal/miss dots followed by empty dots.
// Uses SVG assets via background-image so the strip stays lightweight
// and can be scaled without JS.
// ------------------------------------------------------------------
function DotStrip({ side, resolved, empty }) {
  const items = [...resolved, ...Array(empty).fill('empty')];
  return (
    <div
      className={`kk-penalty-hud__dots ${side === 'right' ? 'kk-penalty-hud__dots--right' : ''}`}
      data-testid={`penalty-dots-${side}`}
    >
      {items.map((kind, i) => (
        <span
          key={`${side}-${i}`}
          className="kk-penalty-hud__dot"
          style={{
            backgroundImage: `url(${
              kind === 'goal'
                ? A.penaltyDotGoal
                : kind === 'miss'
                  ? A.penaltyDotMiss
                  : A.penaltyDotEmpty
            })`,
          }}
        />
      ))}
    </div>
  );
}

// ------------------------------------------------------------------
// RevealBlock — same static composition as before but sized for the
// new cinematic canvas. Cards use PlayingCard (unchanged). Outcome
// text uses GOAL (green) or SAVE (red). MISS is reserved for future
// engine states — currently not emitted.
// ------------------------------------------------------------------
function RevealBlock({ lastShot, matchFinished }) {
  const outcome = lastShot.outcome;
  return (
    <motion.div
      key={`${outcome}-${lastShot.attackCard?.id}-${lastShot.defenseCard?.id}`}
      initial={{ scale: 0.88, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.32 }}
      className="kk-penalty-reveal"
    >
      <div className="kk-penalty-reveal__pair">
        <div className="kk-penalty-reveal__card">
          <PlayingCard card={lastShot.attackCard} highlight={outcome === 'goal'} />
        </div>
        <div className="kk-penalty-reveal__vs">vs</div>
        <div className="kk-penalty-reveal__card">
          <PlayingCard card={lastShot.defenseCard} highlight={outcome === 'save'} />
        </div>
      </div>
      <div
        data-testid="penalty-reveal-outcome"
        className={`kk-penalty-reveal__outcome ${
          outcome === 'goal'
            ? 'kk-penalty-reveal__outcome--goal'
            : 'kk-penalty-reveal__outcome--save'
        }`}
      >
        {outcome === 'goal' ? 'GOAL!' : 'SAVE!'}
      </div>
      {matchFinished && (
        <div className="kk-penalty-reveal__match-decided">MATCH DECIDED</div>
      )}
    </motion.div>
  );
}
